import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

export function createWritableBoard({
    scene,
    objectsRef,
    selectedObjectRef,
    url,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = [1, 1, 1],
    heightAboveFloor = 0,
    writableMeshName,
    textureLoader,
}) {
    const loader = new GLTFLoader();

    loader.load(url, (gltf) => {
        const rawModel = gltf.scene;

        const wrapper = new THREE.Group();

        rawModel.scale.set(...scale);
        rawModel.updateMatrixWorld(true);

        const box = new THREE.Box3().setFromObject(rawModel);
        rawModel.position.y -= box.min.y;

        wrapper.add(rawModel);

        wrapper.position.set(position[0], position[1] + heightAboveFloor, position[2]);
        wrapper.rotation.set(...rotation);

        const axes = new THREE.AxesHelper(0.5);
        wrapper.add(axes);

        let writableMesh = null;
        rawModel.traverse((child) => {
            if (child.isMesh) {
                child.userData.isSelectable = true;
                if (child.name === writableMeshName) writableMesh = child;
            }
        });

        if (!writableMesh) {
            console.warn(`Mesh ${writableMeshName} not found`);
            return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");

        const clearCanvas = () => {
            ctx.fillStyle = "#1e1e1e";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        };
        clearCanvas();

        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;
        texture.offset.x = 1;

        writableMesh.material = new THREE.MeshStandardMaterial({ map: texture });
        writableMesh.userData = { canvas, ctx, texture, clearCanvas };

        wrapper.userData = {
            isModel: true,
            isWritableBoard: true,
            lines: [""],
            writableMeshName,
            isSelectable: true,
            isMovable: true,
            mode: "move",
        };

        scene.add(wrapper);
        objectsRef.current.push(wrapper);
        selectedObjectRef.current = wrapper;

        const interval = setInterval(() => {
            const mesh = wrapper.getObjectByName(writableMeshName);
            if (!mesh) return;

            if (selectedObjectRef.current === wrapper && wrapper.userData.mode === "write") {
                mesh.material.emissive.set(0x4da3ff);
                mesh.material.emissiveIntensity = 0.3;
            } else {
                mesh.material.emissive.set(0x000000);
                mesh.material.emissiveIntensity = 0;
            }
        }, 100);

        const createRadialMenu = () => {
            if (!scene) return;
            if (wrapper.userData.radialMenu) {
                scene.remove(wrapper.userData.radialMenu);
                wrapper.userData.radialMenu = null;
            }

            const radius = 120;
            const group = new THREE.Group();
            group.userData.isRadialMenu = true;

            const actions = [
                {
                    fn: () => { wrapper.userData.mode = "write"; wrapper.userData.isMovable = false; },
                    icon: "/icons/write.png",
                },
                {
                    fn: () => { wrapper.userData.mode = "move"; wrapper.userData.isMovable = true; },
                    icon: "/icons/move.png",
                },
            ];

            actions.forEach((action, i) => {
                const angle = (i / actions.length) * Math.PI * 2;
                const geometry = new THREE.CircleGeometry(20, 32);
                const material = new THREE.MeshBasicMaterial({ color: 0x6cc6d9, depthTest: false });
                const mesh = new THREE.Mesh(geometry, material);
                mesh.rotation.x = -Math.PI / 2;
                mesh.position.set(Math.cos(angle) * radius, 1, Math.sin(angle) * radius);

                if (textureLoader) {
                    const texture = textureLoader.load(action.icon);
                    const iconGeo = new THREE.PlaneGeometry(30, 30);
                    const iconMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, side: THREE.DoubleSide });
                    const iconMesh = new THREE.Mesh(iconGeo, iconMat);
                    iconMesh.rotation.x = Math.PI;
                    iconMesh.rotation.z = -Math.PI / 2;
                    iconMesh.position.set(0, 0.1, 0);
                    mesh.add(iconMesh);
                }

                mesh.userData.onClick = action.fn;
                mesh.userData.isRadialButton = true;
                group.add(mesh);
            });

            scene.add(group);
            group.position.copy(wrapper.position);
            group.position.y = 1;
            wrapper.userData.radialMenu = group;
        };
        createRadialMenu();

        if (!window._writableBoardListener) {
            window._writableBoardListener = (e) => {
                const model = selectedObjectRef.current;
                if (!model?.userData?.isWritableBoard) return;
                if (model.userData.mode !== "write") return;

                const mesh = model.getObjectByName(model.userData.writableMeshName);
                if (!mesh) return;

                const { canvas, ctx, texture, clearCanvas } = mesh.userData;
                const lines = model.userData.lines;

                const fontSize = 48;
                const lineSpacing = fontSize * 1.2;

                if (e.key === "Backspace") {
                    let last = lines[lines.length - 1];
                    if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
                    else if (lines.length > 1) lines.pop();
                } else if (e.key === "Enter") {
                    lines.push("");
                } else if (e.key.length === 1) {
                    lines[lines.length - 1] += e.key;
                } else return;

                clearCanvas();
                ctx.fillStyle = "white";
                ctx.font = `${fontSize}px Arial`;
                ctx.textAlign = "left";
                ctx.textBaseline = "top";

                lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * lineSpacing));
                texture.needsUpdate = true;
            };
            window.addEventListener("keydown", window._writableBoardListener);
        }

        wrapper.userData.dispose = () => {
            if (wrapper.userData.radialMenu) scene.remove(wrapper.userData.radialMenu);
            scene.remove(wrapper);
            clearInterval(interval);
        };
    },
        undefined,
        (err) => console.error("GLTF load error:", err)
    );
}


// import * as THREE from "three";
// import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

// /**
//  * Tworzy WritableBoard w scenie z pełną logiką pisania,
//  * pivotem na dole, radial menu i globalnym listenerem.
//  */
// export function createWritableBoard({
//     scene,
//     objectsRef,
//     selectedObjectRef,
//     url,
//     position = [0, 0, 0],
//     rotation = [0, 0, 0],
//     scale = [1, 1, 1],
//     heightAboveFloor = 0,
//     writableMeshName,
//     textureLoader,
// }) {
//     const loader = new GLTFLoader();

//     loader.load(
//         url,
//         (gltf) => {
//             const rawModel = gltf.scene;

//             // Wrapper = nowy pivot na dole
//             const wrapper = new THREE.Group();

//             // Skala i update
//             rawModel.scale.set(...scale);
//             rawModel.updateMatrixWorld(true);

//             // Bounding box i przesunięcie modelu lokalnie
//             const box = new THREE.Box3().setFromObject(rawModel);
//             rawModel.position.y -= box.min.y;

//             // Dodajemy model do wrappera
//             wrapper.add(rawModel);

//             // Ustawienie wrappera na podłodze
//             wrapper.position.set(position[0], position[1] + heightAboveFloor, position[2]);
//             wrapper.rotation.set(...rotation);

//             // AxesHelper pokazujący pivot na dole
//             const axes = new THREE.AxesHelper(0.5);
//             wrapper.add(axes);

//             // Wyszukiwanie writableMesh
//             let writableMesh = null;
//             rawModel.traverse((child) => {
//                 if (child.isMesh) {
//                     child.userData.isSelectable = true;
//                     if (child.name === writableMeshName) writableMesh = child;
//                 }
//             });
//             if (!writableMesh) {
//                 console.warn(`Mesh ${writableMeshName} not found`);
//                 return;
//             }

//             // Canvas + tekstura do pisania
//             const canvas = document.createElement("canvas");
//             canvas.width = 1024;
//             canvas.height = 512;
//             const ctx = canvas.getContext("2d");
//             const clearCanvas = () => {
//                 ctx.fillStyle = "#1e1e1e";
//                 ctx.fillRect(0, 0, canvas.width, canvas.height);
//             };
//             clearCanvas();

//             const texture = new THREE.CanvasTexture(canvas);
//             texture.wrapS = THREE.RepeatWrapping;
//             texture.repeat.x = -1;
//             texture.offset.x = 1;

//             writableMesh.material = new THREE.MeshStandardMaterial({ map: texture });
//             writableMesh.userData = { canvas, ctx, texture, clearCanvas };

//             // userData wrappera
//             wrapper.userData = {
//                 isModel: true,
//                 isWritableBoard: true,
//                 lines: [""],
//                 writableMeshName,
//                 isSelectable: true,
//                 isMovable: true,
//                 mode: "move",
//             };

//             // Dodanie do sceny
//             scene.add(wrapper);
//             objectsRef.current.push(wrapper);
//             selectedObjectRef.current = wrapper;

//             // Emissive highlight
//             const interval = setInterval(() => {
//                 const model = wrapper;
//                 if (!model) return;
//                 const mesh = model.getObjectByName(writableMeshName);
//                 if (!mesh) return;

//                 if (selectedObjectRef.current === model && model.userData.mode === "write") {
//                     mesh.material.emissive.set(0x4da3ff);
//                     mesh.material.emissiveIntensity = 0.3;
//                 } else {
//                     mesh.material.emissive.set(0x000000);
//                     mesh.material.emissiveIntensity = 0;
//                 }
//             }, 100);

//             // Radial menu
//             const createRadialMenu = () => {
//                 if (!scene) return;
//                 if (wrapper.userData.radialMenu) {
//                     scene.remove(wrapper.userData.radialMenu);
//                     wrapper.userData.radialMenu = null;
//                 }

//                 const radius = 120;
//                 const group = new THREE.Group();
//                 group.userData.isRadialMenu = true;

//                 const actions = [
//                     {
//                         fn: () => {
//                             wrapper.userData.mode = "write";
//                             wrapper.userData.isMovable = false;
//                         },
//                         icon: "/icons/write.png",
//                     },
//                     {
//                         fn: () => {
//                             wrapper.userData.mode = "move";
//                             wrapper.userData.isMovable = true;
//                         },
//                         icon: "/icons/move.png",
//                     },
//                 ];

//                 actions.forEach((action, i) => {
//                     const angle = (i / actions.length) * Math.PI * 2;
//                     const geometry = new THREE.CircleGeometry(20, 32);
//                     const material = new THREE.MeshBasicMaterial({ color: 0x6cc6d9, depthTest: false });
//                     const mesh = new THREE.Mesh(geometry, material);
//                     mesh.rotation.x = -Math.PI / 2;
//                     mesh.position.set(Math.cos(angle) * radius, 1, Math.sin(angle) * radius);

//                     if (textureLoader) {
//                         const texture = textureLoader.load(action.icon);
//                         const iconGeo = new THREE.PlaneGeometry(30, 30);
//                         const iconMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, side: THREE.DoubleSide });
//                         const iconMesh = new THREE.Mesh(iconGeo, iconMat);
//                         iconMesh.rotation.x = Math.PI;
//                         iconMesh.rotation.z = -Math.PI / 2;
//                         iconMesh.position.set(0, 0.1, 0);
//                         mesh.add(iconMesh);
//                     }

//                     mesh.userData.onClick = action.fn;
//                     mesh.userData.isRadialButton = true;
//                     group.add(mesh);
//                 });

//                 scene.add(group);
//                 group.position.copy(wrapper.position);
//                 group.position.y = 1;

//                 wrapper.userData.radialMenu = group;
//             };
//             createRadialMenu();

//             // =========================
//             // GLOBALNY KEYDOWN (tylko jeden listener)
//             // =========================
//             if (!window._writableBoardListener) {
//                 let lastKey = null;

//                 window._writableBoardListener = (e) => {
//                     // ignoruj powtórki tego samego klawisza w tym samym ticku
//                     if (e.key === lastKey) return;
//                     lastKey = e.key;

//                     const model = selectedObjectRef.current;
//                     if (!model?.userData?.isWritableBoard) return;
//                     if (model.userData.mode !== "write") return;

//                     const mesh = model.getObjectByName(model.userData.writableMeshName);
//                     if (!mesh) return;

//                     const { canvas, ctx, texture, clearCanvas } = mesh.userData;
//                     const lines = model.userData.lines;

//                     if (e.key === "Backspace") {
//                         let last = lines[lines.length - 1];
//                         if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
//                         else if (lines.length > 1) lines.pop();
//                     } else if (e.key === "Enter") {
//                         lines.push("");
//                     } else if (e.key.length === 1) {
//                         lines[lines.length - 1] += e.key;
//                     } else return;

//                     clearCanvas();
//                     ctx.fillStyle = "white";
//                     ctx.font = "48px Arial";
//                     ctx.textAlign = "left";
//                     ctx.textBaseline = "top";

//                     lines.forEach((line, i) => ctx.fillText(line, 40, 40 + i * 26));
//                     texture.needsUpdate = true;

//                     // resetuj blokadę po małym timeout
//                     setTimeout(() => {
//                         lastKey = null;
//                     }, 5);
//                 };

//                 window.addEventListener("keydown", window._writableBoardListener);
//             }

//             // Cleanup przy usuwaniu tablicy (opcjonalnie)
//             wrapper.userData.dispose = () => {
//                 if (wrapper.userData.radialMenu) scene.remove(wrapper.userData.radialMenu);
//                 scene.remove(wrapper);
//                 clearInterval(interval);
//             };
//         },
//         undefined,
//         (err) => console.error("GLTF load error:", err)
//     );
// }